/**
 * Public page for a scoped share link: /s#<token>.
 *
 * The token lives in the URL FRAGMENT, which browsers never send to a server
 * (so it is in no access log and no Referer) and is posted to /share/view in a
 * request body. Revoked, expired and unknown links all read "not available".
 */

import { useEffect, useState } from 'react';
import { readShareToken, useViewSharedItemsQuery } from '../store/api/sharingApi';
import { SharedItemsView } from '../components/profile/SharedItemsView';
import { SEO } from '../components/seo';

export function SharedItemsPage() {
  const [token, setToken] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : readShareToken(window.location.hash),
  );

  useEffect(() => {
    const onHash = () => setToken(readShareToken(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const { data, isLoading, isError } = useViewSharedItemsQuery(token ?? '', { skip: !token });

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Shared with you" noIndex />
      <div className="max-w-2xl mx-auto px-4 py-10">
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-6">Shared privately on BeHeard</p>
        {!token || isError ? (
          <div className="card" data-testid="share-not-found">
            <div className="card-body">
              <h1 className="text-xl font-semibold text-gray-900">This link is not available</h1>
              <p className="text-gray-500 mt-1">It may have expired or been revoked by its owner.</p>
            </div>
          </div>
        ) : isLoading || !data ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : (
          <SharedItemsView items={data.items} expiresAt={data.expiresAt} />
        )}
      </div>
    </div>
  );
}
